import PropTypes from 'prop-types';
// material
import {
  Card,
  CardHeader,
  CardContent,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  IconButton,
  Stack,
  Tooltip
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import TvIcon from '@mui/icons-material/Tv';
import ConnectedTvIcon from '@mui/icons-material/ConnectedTv';
import BetaChip from '../../common/BetaChip';
import { useIsFeatureFlagEnabled } from '../../../utils/FeatureFlags';
import SetRackPowerButton from './SetRackPowerButton';

// determines whether LRU has Remote View capabilities
function isRemoteViewEnabled(lru) {
  const lrusWithRemoteView = ['smartmonitor', 'ped', 'qasb'];
  return lrusWithRemoteView.includes(lru);
}

LRUInfo.propTypes = {
  rackDetails: PropTypes.object,
  jobInProgress: PropTypes.bool
};

function formatMemory(bytes) {
  if (!bytes) return '-';
  const MB = bytes / 1_000_000;
  return `${Math.round(MB)}`;
}

function formatCPU(milli) {
  if (!milli) return '-';
  return `${(milli / 1000).toFixed(2)}`;
}

export default function LRUInfo({ rackDetails, jobInProgress }) {
  const isWebrtcEnabled = useIsFeatureFlagEnabled('webrtc');
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title="LRUs" />
      <CardContent>
        <TableContainer component={Paper} data-testid="lru-info">
          <Table aria-label="simple table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>LRU</TableCell>
                <TableCell align="right">IP</TableCell>
                <TableCell align="right">Unit</TableCell>
                <TableCell align="right">Master LRU</TableCell>
                <TableCell align="right">CPU (Cores)</TableCell>
                <TableCell align="right">Memory (MB)</TableCell>
                <TableCell align="right">Controls</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rackDetails.lrus
                ? rackDetails.lrus.map((row, i) => (
                    <TableRow key={i} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        {row.type.toUpperCase()}
                        {row.type === 'smartmonitor' ? <BetaChip sx={{ ml: 1 }} /> : null}
                      </TableCell>
                      <TableCell align="right">{row.ip}</TableCell>
                      <TableCell align="right">{row.unit}</TableCell>
                      <TableCell align="right">{row.is_master.toString()}</TableCell>
                      <TableCell align="right">{formatCPU(row.vm_resources?.cpu_limit)}</TableCell>
                      <TableCell align="right">
                        {formatMemory(row.vm_resources?.memory_limit)}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end" alignItems="center">
                          <SetRackPowerButton
                            rackDetails={rackDetails}
                            jobInProgress={jobInProgress}
                            type={row}
                            endpoint="/racks/lrus/running"
                            label="LRU"
                            buildPayload={(lru) => ({
                              lrus: [
                                {
                                  type: lru.type,
                                  units: [lru.unit]
                                }
                              ]
                            })}
                          />
                          {isRemoteViewEnabled(row.type) && row.type === 'smartmonitor' && (
                            <Tooltip title="connect using vnc">
                              <IconButton
                                data-testid="vnc-button"
                                size="small"
                                variant="contained"
                                component={Link}
                                to={`/vsm/${rackDetails.name}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <TvIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isRemoteViewEnabled(row.type) && isWebrtcEnabled && (
                            <Tooltip title="connect using webrtc">
                              <IconButton
                                data-testid="webrtc-button"
                                size="small"
                                variant="contained"
                                component={Link}
                                to={`/webrtc/${rackDetails.name}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ConnectedTvIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                : [0, 1].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        <Skeleton />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton />
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
